import {
    ITEM_TIER_WEIGHTS,
    MAX_UPGRADES_PER_PACK,
    RARITY_TIER_INDEX,
    QUALITY_TIER_THRESHOLDS,
    ITEMS_PER_PACK
} from '../config/balance.js';

// Waves generation and rarity system
export class PackGenerator {
    constructor(itemsConfig, gameState, itemGenerator) {
        this.itemsConfig = itemsConfig;
        this.gameState = gameState;
        this.itemGenerator = itemGenerator;
        // Get all base item IDs from generator config
        this.allBaseItemIds = itemGenerator.baseItems.map(item => item.id);
    }

    /**
     * Tier-based weight tables for item rarity distribution.
     * @deprecated Use ITEM_TIER_WEIGHTS from balance.js directly
     */
    static TIER_WEIGHTS = ITEM_TIER_WEIGHTS;

    /**
     * Maximum number of items per pack that can "upgrade" above the lot tier.
     * @deprecated Use MAX_UPGRADES_PER_PACK from balance.js directly
     */
    static MAX_UPGRADES_PER_PACK = MAX_UPGRADES_PER_PACK;

    /**
     * Get pack tier name from quality value
     * @param {number} packQuality - Quality value (0.0 to 1.0)
     * @returns {string} - Tier name
     */
    getPackTier(packQuality) {
        if (packQuality >= QUALITY_TIER_THRESHOLDS.vectorized) return 'vectorized';
        if (packQuality >= QUALITY_TIER_THRESHOLDS.golden) return 'golden';
        if (packQuality >= QUALITY_TIER_THRESHOLDS.pristine) return 'pristine';
        if (packQuality >= QUALITY_TIER_THRESHOLDS.standard) return 'standard';
        return 'faded';
    }

    /**
     * Calculate rarity weights based on pack quality
     * Uses direct tier weights - no interpolation
     * A "Standard" lot uses Standard weights, period.
     * @param {number} packQuality - Quality value (0.0 to 1.0)
     * @returns {object} - Weights for each rarity
     */
    calculateRarityWeights(packQuality) {
        const tier = this.getPackTier(packQuality);
        return { ...PackGenerator.TIER_WEIGHTS[tier] };
    }

    // Weighted random selection of rarity
    rollRarity(weights) {
        const rand = Math.random() * 100;
        let cumulative = 0;

        const rarities = ['faded', 'standard', 'pristine', 'golden', 'vectorized'];
        for (const rarity of rarities) {
            cumulative += weights[rarity];
            if (rand <= cumulative) {
                return rarity;
            }
        }

        return 'faded'; // Fallback
    }

    /**
     * Rarity tier indices for upgrade tracking.
     * @deprecated Use RARITY_TIER_INDEX from balance.js directly
     */
    static RARITY_TIER_INDEX = RARITY_TIER_INDEX;

    /**
     * Generate a pack of items
     * @param {number} packQuality - Quality value (0.0 to 1.0) determines rarity odds
     * @param {string|null} slotType - Optional slot type filter for type-specific lots
     * @returns {array} - Array of generated items
     */
    generatePack(packQuality, slotType = null) {
        const weights = this.calculateRarityWeights(packQuality);
        const packItems = [];

        // Get lot tier for upgrade tracking
        const lotTier = this.getPackTier(packQuality);
        const lotTierIndex = PackGenerator.RARITY_TIER_INDEX[lotTier];
        let upgradesUsed = 0;

        // Type-specific lots use slot-filtered item pool
        const slotLabel = slotType ? ` [${slotType.toUpperCase()} LOT]` : '';
        console.log(`[PackGenerator] Generating pack: quality=${packQuality.toFixed(3)}, tier=${lotTier}${slotLabel}, weights=`, weights);

        // Generate unique base items (avoid duplicates in same pack)
        const usedBaseIds = new Set();

        // Get available items - filter by slot if type-specific
        const registry = this.itemGenerator.getRegistry();
        const allItems = slotType
            ? registry.getBySlot(slotType)
            : registry.getAll();
        const allBaseItemIds = allItems.map(item => item.id);

        for (let i = 0; i < ITEMS_PER_PACK; i++) {
            // Roll rarity for this item
            let rarity = this.rollRarity(weights);
            const originalRarity = rarity;

            // Check if this would be an upgrade (rarity > lot tier)
            const rarityIndex = PackGenerator.RARITY_TIER_INDEX[rarity];
            if (rarityIndex > lotTierIndex) {
                if (upgradesUsed >= PackGenerator.MAX_UPGRADES_PER_PACK) {
                    // Already used max upgrades, force to lot tier
                    console.log(`[PackGenerator] Item ${i}: rolled ${originalRarity} but FORCING to ${lotTier} (max upgrades reached)`);
                    rarity = lotTier;
                } else {
                    // This counts as an upgrade
                    upgradesUsed++;
                    console.log(`[PackGenerator] Item ${i}: UPGRADE to ${rarity} (upgrade ${upgradesUsed}/${PackGenerator.MAX_UPGRADES_PER_PACK})`);
                }
            } else {
                console.log(`[PackGenerator] Item ${i}: ${rarity} (matches or below lot tier)`);
            }

            // Get available base items (not already used in this pack, respecting slot filter)
            const availableBaseItems = allBaseItemIds.filter(id => !usedBaseIds.has(id));

            if (availableBaseItems.length === 0) {
                // If we've used all base items in this slot, allow duplicates
                console.warn(`All base items used in pack${slotType ? ` (${slotType} slot)` : ''}, allowing duplicates`);
                break;
            }

            // Pick random base item
            const baseItemId = availableBaseItems[Math.floor(Math.random() * availableBaseItems.length)];
            usedBaseIds.add(baseItemId);

            // Generate the actual item with random stats
            // Pass packQuality so items that "jump" rarities get star-capped
            const generatedItem = this.itemGenerator.generateItem(baseItemId, rarity, packQuality);

            if (generatedItem) {
                packItems.push(generatedItem);
            }
        }

        return packItems;
    }

    // Get pack data for a given milestone
    getPackDataForSpecificMilestone(milestoneNumber) {
        const milestones = this.itemsConfig.packMilestones;

        // Find exact milestone match
        const exactMatch = milestones.find(m => m.totalMiles === milestoneNumber);
        if (exactMatch) {
            return exactMatch;
        }

        // If not in predefined milestones, generate it
        const generation = this.itemsConfig.milestoneGeneration;
        if (generation.enabled && milestoneNumber >= generation.startAfterMiles) {
            const lastMilestone = milestones[milestones.length - 1];
            const milesAboveStart = milestoneNumber - generation.startAfterMiles;
            const interval = generation.milesInterval;
            const milestoneIndex = Math.floor(milesAboveStart / interval);

            // Cost as percentage of total miles (always attainable if saving)
            // ~25% of milestone means you need to save a quarter of your earnings
            const costPercentage = generation.costPercentage || 0.25;
            const cost = Math.floor(milestoneNumber * costPercentage);
            const quality = Math.min(
                generation.maxQuality,
                lastMilestone.packQuality + (generation.qualityIncrement * (milestoneIndex + 1))
            );

            return {
                totalMiles: milestoneNumber,
                cost,
                packQuality: quality
            };
        }

        return null;
    }

    getPackDataForMilestone(totalMiles) {
        const milestones = this.itemsConfig.packMilestones;
        const generation = this.itemsConfig.milestoneGeneration;

        // Find matching predefined milestone
        for (let i = milestones.length - 1; i >= 0; i--) {
            if (totalMiles >= milestones[i].totalMiles) {
                return milestones[i];
            }
        }

        // If no predefined milestone found and autogeneration is enabled
        if (generation.enabled && totalMiles >= generation.startAfterMiles) {
            // Calculate generated milestone
            const lastMilestone = milestones[milestones.length - 1];
            const milesAboveStart = totalMiles - generation.startAfterMiles;
            const interval = generation.milesInterval;
            const milestoneIndex = Math.floor(milesAboveStart / interval);

            // Calculate the actual milestone number for this index
            const milestoneNumber = generation.startAfterMiles + (interval * (milestoneIndex + 1));

            // Cost as percentage of total miles (always attainable if saving)
            const costPercentage = generation.costPercentage || 0.25;
            const cost = Math.floor(milestoneNumber * costPercentage);
            const quality = Math.min(
                generation.maxQuality,
                lastMilestone.packQuality + (generation.qualityIncrement * (milestoneIndex + 1))
            );

            return {
                totalMiles: milestoneNumber,
                cost,
                packQuality: quality
            };
        }

        return null;
    }

    // Get next milestone after current totalMiles
    getNextMilestone(totalMiles) {
        const milestones = this.itemsConfig.packMilestones;
        const generation = this.itemsConfig.milestoneGeneration;

        // Check predefined milestones
        for (const milestone of milestones) {
            if (totalMiles < milestone.totalMiles) {
                return milestone.totalMiles;
            }
        }

        // If autogeneration enabled, calculate next generated milestone
        if (generation.enabled) {
            const lastMilestone = milestones[milestones.length - 1];
            const interval = generation.milesInterval;
            const nextMilestone = Math.ceil((totalMiles - generation.startAfterMiles) / interval) * interval + generation.startAfterMiles;

            // Make sure it's actually after current miles
            if (nextMilestone <= totalMiles) {
                return nextMilestone + interval;
            }

            return nextMilestone;
        }

        return null; // No more milestones
    }
}
